import React, { useState, useEffect, useMemo } from 'react';
import {
  useTable,
  useRowSelect,
  usePagination,
  useSortBy,
  useFilters
} from 'react-table';
import config from '../../config/config';
import axios from 'axios';
import { matchSorter } from 'match-sorter';
import './Table.css';
import DownloadButton from './DownloadButton';

export const MeetingTable = ({ columns }) => {
  const [data, setData] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  const fileName = 'MeetingsInfo';
  const meeting_headers = [
    { label: 'Meeting Name', key: 'meeting_name' },
    { label: 'Type', key: 'meeting_type' },
    { label: 'Location', key: 'meeting_location' },
    { label: 'Date', key: 'date_time' },
    { label: 'Meeting Document', key: 's3_prefix' },
    { label: 'Notes', key: 'notes' }
  ];

  async function getData(ender) {
    await axios
      .get(config.apiURL + ender, {
        headers: {
          'Content-Type': 'application/json',
          token: config.getToken()
        }
      })
      .then((response) => {
        setData(
          response.data.map((x) => {
            const keys = Object.keys(x);
            const entries = keys.map((key) => {
              if (
                typeof x[key] === 'object' &&
                x[key]?.hasOwnProperty('String')
              ) {
                return [key, x[key].String];
              } else {
                return [key, x[key]];
              }
            });
            const test = Object.fromEntries(entries);
            return test;
          })
        );
        setLoadingData(false);
      });
  }

  useEffect(() => {
    let all_end = '/meetings/all';
    getData(all_end);

    if (loadingData) {
      //if the result is not ready so you make the axios call
      getData(all_end);
    }
  }, [loadingData]); // eslint-disable-line react-hooks/exhaustive-deps

  const filterTypes = useMemo(
    () => ({
      // Add a new fuzzyTextFilterFn filter type.
      fuzzyText: fuzzyTextFilterFn,
      // Or, override the default text filter to use
      // "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter((row) => {
          const rowValue = row.values[id];
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      }
    }),
    []
  );

  // Define a default UI for filtering
  function DefaultColumnFilter({
    column: { filterValue, preFilteredRows, setFilter }
  }) {
    const count = preFilteredRows.length;

    return (
      <input
        value={filterValue || ''}
        onChange={(e) => {
          setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
        }}
        placeholder={`Search ${count} records...`}
      />
    );
  }

  function fuzzyTextFilterFn(rows, id, filterValue) {
    return matchSorter(rows, filterValue, { keys: [(row) => row.values[id]] });
  }

  // Let the table remove the filter if the string is empty
  fuzzyTextFilterFn.autoRemove = (val) => !val;

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter
    }),
    []
  );

  //Table Instance
  const tableInstance = useTable(
    {
      columns,
      data,
      defaultColumn, // Be sure to pass the defaultColumn option
      filterTypes,
      initialState: { pageIndex: 0 }
    },
    useFilters, // useFilters!
    useSortBy,
    usePagination,
    useRowSelect
  );

  //Set table variables
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page, //Begins the pagination and select stuff
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex }
  } = tableInstance;

  return (
    <div style={{ flexGrow: '8', marginLeft: '1px', marginRight: '1px' }}>
      {loadingData ? (
        <p>Loading Please wait...</p>
      ) : (
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                    {column.render('Header')}
                    <div>
                      {column.canFilter ? column.render('Filter') : null}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    if (cell.column.id === 's3_prefix') {
                      return (
                        <td {...cell.getCellProps()}>
                          <a
                            style={{ color: 'blue' }}
                            href={`/fileExplorer?s3_prefix=${cell.value}`}
                          >
                            View
                          </a>
                        </td>
                      );
                    } else {
                      return (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      );
                    }
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <div className='pagination'>
        <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
          {'<<'}
        </button>
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>
          {'<'}
        </button>
        <button onClick={() => nextPage()} disabled={!canNextPage}>
          {'>'}
        </button>
        <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
          {'>>'}
        </button>
        <span>
          Page
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </span>
        <span>
          | Go to page:
          <input
            type='number'
            defaultValue={pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              gotoPage(page);
            }}
            style={{ width: '100px' }}
          />
        </span>
      </div>
      <DownloadButton
        new_data={rows}
        headers={meeting_headers}
        file={fileName}
        dataType='react-table'
      />
    </div>
  );
};
